import { useEffect, useState } from "react";
import { ExternalLink, Menu, X } from "lucide-react";
import { Link, NavLink, useLocation } from "react-router-dom";

import { useMemberToken } from "@/hooks/useMemberAuth";

const navBase =
  "font-body text-body transition-colors duration-150 hover:text-primary";

// Eine Liste für Desktop-Leiste und Burger-Menü — sonst laufen beide auseinander.
const internalLinks = [
  { to: "/", label: "Räume", end: true },
  { to: "/kalender", label: "Gesamtübersicht", end: false },
];
const externalLinks = [
  { href: "https://www.tanzschule-tanzraum.de", label: "Tanzschule" },
  { href: "https://tanzschule-tanzraum.de/ueber-uns/stundenplan/", label: "Stundenplan" },
];

export function Header() {
  const token = useMemberToken();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Nach jedem Seitenwechsel zu — sonst verdeckt das Menü die neue Seite.
  useEffect(() => setOpen(false), [location.pathname]);

  const accountLinks = token
    ? [
        { to: "/konto/buchungen", label: "Mein Konto", end: false },
        { to: "/konto/profil", label: "Profil", end: false },
      ]
    : [{ to: "/konto/login", label: "Login", end: false }];

  return (
    <header className="sticky top-0 z-40 border-b border-line bg-surface">
      <div className="mx-auto flex h-20 max-w-container items-center justify-between px-6">
        <Link
          to="/"
          className="font-display text-h3 uppercase tracking-tight text-primary"
        >
          Tanzraum&nbsp;Köln
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {internalLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) =>
                isActive
                  ? `${navBase} border-b-2 border-primary pb-1 font-semibold text-primary`
                  : `${navBase} text-ink-soft`
              }
            >
              {l.label}
            </NavLink>
          ))}
          {externalLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className={`${navBase} text-ink-soft`}
              target="_blank"
              rel="noreferrer"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {token ? (
            <>
              <Link
                to="/konto/profil"
                className="hidden items-center text-body-sm font-medium text-ink-soft transition-colors hover:text-primary md:inline-flex"
              >
                Profil
              </Link>
              <Link
                to="/konto/buchungen"
                className="hidden items-center rounded border border-line bg-surface px-4 py-2 text-body-sm font-semibold text-ink transition-colors hover:border-primary hover:text-primary md:inline-flex"
              >
                Mein Konto
              </Link>
            </>
          ) : (
            <Link
              to="/konto/login"
              className="hidden items-center rounded border border-line bg-surface px-4 py-2 text-body-sm font-semibold text-ink transition-colors hover:border-primary hover:text-primary md:inline-flex"
            >
              Login
            </Link>
          )}
          <Link
            to="/"
            className="hidden rounded bg-primary px-5 py-2 text-body-sm font-semibold text-white transition-colors hover:bg-primary-dark md:inline-flex"
          >
            Jetzt buchen
          </Link>

          <button
            type="button"
            className="inline-flex h-11 w-11 items-center justify-center rounded border border-line text-ink transition-colors hover:border-primary hover:text-primary md:hidden"
            aria-label={open ? "Menü schließen" : "Menü öffnen"}
            aria-expanded={open}
            aria-controls="mobile-nav"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="h-6 w-6" aria-hidden /> : <Menu className="h-6 w-6" aria-hidden />}
          </button>
        </div>
      </div>

      {open && (
        <nav id="mobile-nav" className="border-t border-line bg-surface px-6 pb-6 pt-2 md:hidden">
          <ul className="divide-y divide-line">
            {[...internalLinks, ...accountLinks].map((l) => (
              <li key={l.to}>
                <NavLink
                  to={l.to}
                  end={l.end}
                  className={({ isActive }) =>
                    `block py-3 ${navBase} ${isActive ? "font-semibold text-primary" : "text-ink"}`
                  }
                >
                  {l.label}
                </NavLink>
              </li>
            ))}
            {externalLinks.map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  target="_blank"
                  rel="noreferrer"
                  className={`flex items-center gap-2 py-3 ${navBase} text-ink-soft`}
                >
                  {l.label}
                  <ExternalLink className="h-4 w-4" aria-hidden />
                </a>
              </li>
            ))}
          </ul>
          <Link
            to="/"
            className="mt-4 block rounded bg-primary px-5 py-3 text-center text-body-sm font-semibold text-white transition-colors hover:bg-primary-dark"
          >
            Jetzt buchen
          </Link>
        </nav>
      )}
    </header>
  );
}
